/**
 * Every published version: who, when, the note, what it held — and Restore
 * (spec 2026-10-23 §5). Restore publishes that version again as the newest
 * one; the caller confirms first and says what else changes.
 *
 * Used by both tabs: each one says how to read a stored version (`parse`,
 * through the shared validator), how to summarize it and which poster to show.
 */
import { History, RotateCcw } from 'lucide-react';
import { formatDate } from '@/lib/utils';
import type { AdminVersionRow } from '../api';

interface Props<C extends { enabled: boolean }> {
  rows: AdminVersionRow[];
  whoName: (adminId: string) => string;
  canWrite: boolean;
  busy: boolean;
  /** A stored version, or null when it cannot be read (it cannot be restored then). */
  parse: (raw: unknown) => C | null;
  summarize: (config: C) => string;
  posterOf: (config: C) => string | null;
  onRestore: (row: AdminVersionRow, config: C) => void;
  /** Start of the element ids; each tab needs its own. */
  idPrefix: string;
}

export function HistoryList<C extends { enabled: boolean }>({
  rows,
  whoName,
  canWrite,
  busy,
  parse,
  summarize,
  posterOf,
  onRestore,
  idPrefix,
}: Props<C>) {
  const headingId = `${idPrefix}-history-heading`;
  return (
    <section aria-labelledby={headingId} className="rounded-lg border bg-card p-5">
      <h4 id={headingId} className="mb-3 flex items-center gap-2 text-base font-semibold">
        <History className="h-4 w-4" aria-hidden="true" />
        History
      </h4>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing is published yet. Each version you publish shows here.</p>
      ) : (
        <ol className="divide-y">
          {rows.map((row, i) => {
            const config = parse(row.config);
            const poster = config ? posterOf(config) : null;
            return (
              <li key={row.version} className="flex flex-wrap items-start gap-3 py-3">
                {poster ? (
                  <img src={poster} alt="" className="h-14 w-14 shrink-0 rounded-md bg-black object-contain" />
                ) : (
                  <div className="h-14 w-14 shrink-0 rounded-md bg-muted" aria-hidden="true" />
                )}
                <div className="min-w-0 flex-1 space-y-0.5 text-sm">
                  <p className="font-medium">
                    Version {row.version}
                    {config && (
                      <span
                        className={
                          config.enabled
                            ? 'ml-2 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800 dark:bg-green-950 dark:text-green-300'
                            : 'ml-2 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground'
                        }
                      >
                        {config.enabled ? 'On' : 'Off'}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDate(row.created_at)} · {whoName(row.created_by_admin_id)}
                  </p>
                  <p className="text-xs">{config ? summarize(config) : 'This version cannot be read. It cannot be restored.'}</p>
                  {row.note && <p className="text-xs italic">“{row.note}”</p>}
                </div>
                <div className="shrink-0">
                  {i === 0 ? (
                    <span className="text-xs font-medium text-muted-foreground">Newest version</span>
                  ) : (
                    config &&
                    canWrite && (
                      <button
                        type="button"
                        onClick={() => onRestore(row, config)}
                        disabled={busy}
                        aria-label={`Restore version ${row.version}`}
                        className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-50"
                      >
                        <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                        Restore
                      </button>
                    )
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
